// src/contexts/LanguageContext.tsx
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { sourceLanguageTag, availableLanguageTags, setLanguageTag, languageTag } from '@/paraglide/runtime';
import { useShopWhenAvailable } from '@/providers/ShopProvider';

type LanguageContextType = {
  currentLanguage: string;
  changeLanguage: (language: string) => void;
  supportedLanguages: { code: string; name: string; flagIcon: string }[];
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children, shopSlug }: { children: ReactNode, shopSlug?: string }) => {
  const shopContext = useShopWhenAvailable();
  const shop = shopContext?.shop;
  const [currentLanguage, setCurrentLanguage] = useState(languageTag());
  const [supportedLanguages, setSupportedLanguages] = useState<{ code: string; name: string; flagIcon: string }[]>([]);

  const changeLanguage = (language: string) => {
    setLanguageTag(language as typeof sourceLanguageTag);
    setCurrentLanguage(language as typeof sourceLanguageTag);
    localStorage.setItem('preferredLanguage', language);
  };

  useEffect(() => {
    if (shop && Array.isArray(shop.supportedLanguages)) {
      setSupportedLanguages(shop.supportedLanguages);
    }
  }, [shop]);

  useEffect(() => {
    const initializeLanguage = () => {
      const storedLanguage = localStorage.getItem('preferredLanguage');
      if (storedLanguage && availableLanguageTags.includes(storedLanguage as typeof sourceLanguageTag)) {
        changeLanguage(storedLanguage);
        return;
      }
      if (shopSlug === 'bosburcht' && shop && shop.defaultLanguage) {
        changeLanguage(shop.defaultLanguage);
        return;
      }
      const browserLanguage = navigator.language.split('-')[0];
      const newLanguage = availableLanguageTags.includes(browserLanguage as typeof sourceLanguageTag) 
        ? browserLanguage 
        : sourceLanguageTag;
      changeLanguage(newLanguage);
    };
    initializeLanguage();
    // Only run on mount or when shopSlug/shop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopSlug, shop?.defaultLanguage]);

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, supportedLanguages }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};